
import { DirectoryProductCard } from './DirectoryProductCard';
import { DirectoryProductTable } from './DirectoryProductTable';
import { Product } from '@/types/portfolio';
import { ViewMode } from '@/pages/Directory';
import { Card } from '@/components/ui/card';
import { useState } from 'react';

export type SortField = 'name' | 'manufacturer' | 'product_type' | 'certification_status';
export type SortDirection = 'asc' | 'desc';

interface DirectoryResultsProps {
  products: Product[];
  viewMode: ViewMode;
  isLoading: boolean;
}

export const DirectoryResults = ({ 
  products, 
  viewMode, 
  isLoading 
}: DirectoryResultsProps) => {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    let aValue = '';
    let bValue = '';

    switch (sortField) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'manufacturer':
        aValue = a.manufacturer || '';
        bValue = b.manufacturer || '';
        break;
      case 'product_type':
        aValue = a.product_type || '';
        bValue = b.product_type || '';
        break;
      case 'certification_status':
        aValue = a.certification_status || '';
        bValue = b.certification_status || '';
        break;
    }

    const comparison = aValue.localeCompare(bValue);
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Loading certified products...</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          No certified products found matching your criteria.
        </p>
      </Card>
    );
  }

  if (viewMode === 'list') {
    return (
      <DirectoryProductTable 
        products={sortedProducts}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {sortedProducts.map((product) => (
        <DirectoryProductCard key={product.id} product={product} />
      ))}
    </div>
  );
};
